import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'components/common/Loader';
import { ProjectDetails } from '../ConductInspection/components/ProjectDetails';
import { AddIncident } from './components/AddIncident';
import { useGetChecklist } from 'hooks/useGetChecklist';
import { useSetDropdownAndRole } from 'hooks/useSetDropdownAndRole';
import {
  IIncidents,
  useCreateIncidentMutation,
  useGetIncidentByIdMutation,
  useUpdateIncidentPatchMutation,
} from 'redux/services/incident';
import toast from 'react-hot-toast';
import Modal from 'components/common/Modal/Modal';
import {
  getInspectionDataForApi,
  getProjectDetailsFromObservationData,
} from 'pages/utils';
import { setProjectDetails } from 'redux/conductInspectionSlice';
import { useDispatch } from 'react-redux';
import './RecordIncident.scss';
import { useTranslation } from 'react-i18next';

const RecordIncident = () => {
  const { t } = useTranslation();
  const { projectId, incidentId, step = '0' } = useParams();
  const [recIncident, setIncident] = useState<IIncidents | null>(null);
  const [newIncidentId, setNewIncidentId] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [createIncident, { isLoading: createIncidentLoading }] =
    useCreateIncidentMutation();
  const [addIncident, { isLoading: addIncidentLoading }] =
    useUpdateIncidentPatchMutation();
  const [getIncidentById, { data: incident, isLoading: isIncidentsLoading }] =
    useGetIncidentByIdMutation();

  useEffect(() => {
    return () => {
      dispatch(setProjectDetails(null));
    };
  }, [dispatch]);

  useGetChecklist();
  const { project, isLoading } = useSetDropdownAndRole();

  const fetchIncidentDetails = useCallback(async () => {
    await getIncidentById({
      projectId,
      id: incidentId,
    });
  }, [getIncidentById, incidentId, projectId]);

  useEffect(() => {
    if (incidentId && !incident) {
      fetchIncidentDetails();
    }
  }, [fetchIncidentDetails, incidentId, incident]);

  useEffect(() => {
    if (project && incident) {
      dispatch(
        setProjectDetails(
          getProjectDetailsFromObservationData(incident, project),
        ),
      );

      setIncident(incident);
    }
  }, [project, incident, incidentId, dispatch]);

  const renderPage = () => {
    switch (step) {
      case '1':
        return (
          <AddIncident
            incident={recIncident}
            onSubmit={handleSubmitAddIncident}
          />
        );
      default:
        return (
          <ProjectDetails
            project={project!}
            observation
            onSubmit={handleProjectDetailsSubmit}
          />
        );
    }
  };

  const handleProjectDetailsSubmit = async (values: any) => {
    const res: any = await (incidentId
      ? createIncident(
          getInspectionDataForApi({
            ...values,
            projectId,
            _id: incidentId,
          }),
        )
      : createIncident(
          getInspectionDataForApi({
            ...values,
            projectId,
            _id: incidentId,
          }),
        ));
    if (res.data) {
      toast.success(t('save_success'));
      navigate(`/project/${projectId}/record-incident/${res.data._id}/1`);
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleSubmitAddIncident = async (payload: any) => {
    const res: any = await addIncident(payload);

    if (res.data) {
      dispatch(setProjectDetails(null));
      setNewIncidentId(res.data.displayId ?? incidentId);
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleSubmit = () => {
    setNewIncidentId('');
    navigate(`/project/${projectId}/incident`);
  };

  return (
    <div className="record-incident d-flex">
      <div className="page-title">
        <h2 className="page-title">{t('header_record_incident')}</h2>
      </div>
      <div className="d-flex flex-column setup-container">
        {project && renderPage()}
        {(isLoading ||
          createIncidentLoading ||
          addIncidentLoading ||
          isIncidentsLoading) && <Loader />}
      </div>
      <Modal
        show={newIncidentId.length > 0}
        withIcon
        type={'success'}
        title=""
        modalClassName="checklist-modal"
        withIconBodyText={
          <div className="d-flex flex-column">
            <span>
              <b>{t('successfull')}</b>
            </span>
            <span className="h6">{t('incident_id')}</span>
            <span className="h6">{incident?.displayId}</span>
          </div>
        }
        footerContentAlignment="center"
        displayCloseButton={false}
        submitBtnText={t('btn_ok')}
        hideCancelBtn
        onSubmit={handleSubmit}
        onClose={() => {}}
      />
    </div>
  );
};

export default RecordIncident;
